import {
  createAssistantStream,
  type AssistantStream,
  type AssistantStreamController,
} from 'assistant-stream';
import { truncate } from 'lodash';

import { isUnchecked } from '@/lib/pii/unchecked-messages';

import { useAssistantCloudStore } from './stores/assistant-cloud-store';
import { ensureStoreInitialized } from './utils';

export const UNTITLED_THREAD_TITLE = 'New Thread';

export class RunsAPI {
  async stream(body: {
    thread_id: string;
    assistant_id: 'system/thread_title';
    messages: readonly unknown[];
  }): Promise<AssistantStream> {
    const { thread_id, assistant_id, messages } = body;
    return assistant_id === 'system/thread_title'
      ? this.generateTitle(thread_id, messages)
      : createAssistantStream(async (controller: AssistantStreamController) => controller.close());
  }

  async report(_data: unknown): Promise<void> {
    // No-op: local runtime doesn't report telemetry to the cloud
  }

  private async generateTitle(
    threadId: string,
    messages: readonly unknown[],
  ): Promise<AssistantStream> {
    await ensureStoreInitialized();
    const { updateThread } = useAssistantCloudStore.getState();
    const title = this.extractTitleFromMessages(messages);

    await updateThread(threadId, { title }).catch(() => {
      // Thread title update failed - thread may not exist yet
    });

    return createAssistantStream(async (controller: AssistantStreamController) => {
      controller.appendText(title);
      controller.close();
    });
  }

  private extractTitleFromMessages(messages: readonly unknown[]): string {
    // An unchecked message may still be blocked as personal data, so it cannot name the thread.
    const firstUserMessage = messages.find((m: unknown) => {
      const { role, id } = m as Record<string, unknown>;
      return role === 'user' && !(typeof id === 'string' && isUnchecked(id));
    });

    if (!firstUserMessage) {
      return UNTITLED_THREAD_TITLE;
    }

    const content = (firstUserMessage as Record<string, unknown>).content;

    if (typeof content === 'string') {
      return truncate(content, { length: 50, omission: '' });
    }

    if (Array.isArray(content)) {
      const textPart = content.find(
        (part: unknown) => (part as Record<string, unknown>).type === 'text',
      );
      const text = (textPart as Record<string, unknown>)?.text;
      if (typeof text === 'string') {
        return truncate(text, { length: 50, omission: '' });
      }
    }

    return UNTITLED_THREAD_TITLE;
  }
}
